<script setup lang="ts">
import type { Content } from '@bloomreach/spa-sdk'
import { useTemplateRef } from 'vue'
import { useBrxmData } from '../../composables/useBrxmData'
import BannerRow from './parts/BannerRow.vue'
import Banner from './parts/Banner.vue'
import { EMobileOrientation, type IBannerContent, type IBannerRow } from './ts/types'
import { useVisibilityTracking } from '@/cms-ui/composables/useAnalytics'
import useBreakpoint from '@/ui/composables/useBreakpoint'
import type { IRef } from '@/cms-ui/ts/interfaces/IRef'
import type { IBrxmProps } from '@/cms-ui/ts/interfaces/IBrxmProps'
import { getContent } from '@/cms-ui/utils/getContent'

const props = defineProps<IBrxmProps>()
const data = useBrxmData(props, 'BANNERS_LIST')
const { isMobile } = useBreakpoint()
const { rows, mobileOrientation, displayName, mobileHeight, darkmode } = data.value

const bannersListRef = useTemplateRef('bannersListRef')
const bannersWithoutContent: IRef[] = []
const bannersWithContent: IBannerContent[] = []

useVisibilityTracking(bannersListRef, 'BannersList', displayName || 'BannersList')

rows.forEach((row: IBannerRow) => {
    bannersWithoutContent.push(...row.banners)
})

bannersWithoutContent.forEach(item => {
    const content = getContent<Content>(item, props.page)?.getData<IBannerContent>()
    if (content) {
        bannersWithContent.push(content)
    }
})

if (mobileHeight && mobileHeight.value) {
    mobileHeight.value = mobileHeight?.value
        ? mobileHeight?.value?.toLowerCase()?.charAt(0)?.toUpperCase() + mobileHeight?.toLowerCase()?.slice(1)
        : undefined
}
</script>

<template>
    <section
        ref="bannersListRef"
        class="container-grid space-standard container mx-auto gap-4 lg:gap-6"
    >
        <BannerRow
            v-for="(row, index) in rows"
            v-show="!isMobile || (mobileOrientation || '').toLowerCase() !== EMobileOrientation.SWIPE"
            :key="`Banner-row__${index}`"
            :size="
                (row.mobileOrientation || '').toLowerCase() === EMobileOrientation.SWIPE && isMobile
                    ? mobileHeight
                    : row.height
            "
            :page="props.page"
            :layout="row.layout"
            :banners="row.banners"
            :mobile-orientation="row.mobileOrientation"
            :display-name="row.displayName"
            class="col-span-full"
            :darkmode="darkmode"
        />

        <VCarousel
            v-show="isMobile && (mobileOrientation || '').toLowerCase() === EMobileOrientation.SWIPE"
            :carousel-aria-label="`carousel_${displayName}`"
            :data-test="`carousel_${displayName}`"
            class="col-span-full"
            :dark="darkmode"
        >
            <template
                v-for="(banner, index) in bannersWithContent"
                :key="`${banner?.name}_${index}`"
            >
                <Banner
                    :dark="banner?.darkMode"
                    :data-test="`banner_${banner?.id}`"
                    :title="banner?.heading"
                    :paragraph="banner?.paragraph?.value"
                    :size="
                        mobileHeight?.toLowerCase()?.charAt(0)?.toUpperCase() + mobileHeight?.toLowerCase()?.slice(1)
                    "
                    :seo-heading-size="banner?.seoHeadingSize?.value"
                    class="mr-2"
                    :class="{ 'ml-2': index === 0, 'mr-4': index === bannersWithContent.length - 1 }"
                    :image="banner?.image"
                    :image-alt="banner?.image?.altText"
                    :badge="banner?.badge"
                    :button="banner?.button"
                    :background="banner?.background"
                    :page="page"
                    :image-placement-mobile="banner?.mobileImagePosition"
                    :image-placement-desktop="banner?.desktopImagePosition"
                    carousel
                />
            </template>

            <template #bottom>
                <VCarouselScrollbar />
            </template>
        </VCarousel>
    </section>
</template>
